import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Home as HomeIcon, ChevronRight, Tag } from "lucide-react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import WhatsAppButton from "../../components/WhatsAppButton";
import BackToTop from "../../components/BackToTop";
import { useSEO } from "../../lib/useSEO";
import { POSTS as STATIC_POSTS } from "../../lib/blog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndex() {
  const [dbPosts, setDbPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts`)
      .then((res) => setDbPosts(res.data || []))
      .catch(() => setDbPosts([]));
  }, []);

  const allPosts = [
    ...dbPosts.map((p) => ({
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt,
      date: p.created_at ? p.created_at.slice(0, 10) : "",
      readMin: p.read_min,
      category: p.category,
      image: p.image,
    })),
    ...STATIC_POSTS,
  ];

  const canonical = "https://kreuse-exterieur.fr/conseils";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        name: "Conseils K'REUSE EXTÉRIEUR",
        description:
          "Articles pratiques sur le paysagisme, l'entretien de jardin et la lutte contre les nuisibles en Creuse.",
        url: canonical,
        blogPost: allPosts.map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          datePublished: p.date,
          url: `https://kreuse-exterieur.fr/conseils/${p.slug}`,
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://kreuse-exterieur.fr/" },
          { "@type": "ListItem", position: 2, name: "Conseils & actualités", item: canonical },
        ],
      },
    ],
  };

  useSEO({
    title: "Conseils jardin & anti-nuisibles Creuse | K'REUSE EXTÉRIEUR",
    description:
      "Guides pratiques : frelons asiatiques, crédit d'impôt entretien jardin, calendrier de tonte et taille. Conseils d'un paysagiste local en Creuse (23).",
    canonical,
    jsonLd,
  });

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C8E6C9] opacity-40 blur-3xl" />
            <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#E8F5E9] opacity-70 blur-3xl" />
          </div>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#5C705E] mb-6">
              <Link to="/" className="flex items-center gap-1 hover:text-[#1B5E20]">
                <HomeIcon className="w-3.5 h-3.5" />
                Accueil
              </Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-[#1B5E20] font-medium">Conseils & actualités</span>
            </nav>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5EBE5] shadow-sm mb-6">
              <Tag className="w-3.5 h-3.5 text-[#1B5E20]" />
              <span className="text-sm font-medium text-[#1B5E20]">Le blog du paysagiste</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A2E1C] leading-[1.05]">
              Conseils <span className="font-medium text-[#1B5E20]">jardin & nuisibles</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C705E] leading-relaxed max-w-2xl">
              Astuces, calendriers d'entretien et guides pratiques rédigés par un
              professionnel installé en Creuse. Pour entretenir, protéger et
              embellir votre extérieur — toute l'année.
            </p>
          </div>
        </section>

        {/* Posts grid */}
        <section className="pb-24">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/conseils/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group bg-white border border-[#E5EBE5] rounded-3xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      width="1600"
                      height="1067"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-wider text-[#1B5E20] font-semibold mb-3">
                      {p.category}
                    </div>
                    <h2 className="font-['Outfit'] text-xl font-semibold text-[#1A2E1C] leading-snug group-hover:text-[#1B5E20] transition-colors">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-sm text-[#5C705E] leading-relaxed">
                      {p.excerpt}
                    </p>
                    <div className="mt-5 flex items-center justify-between text-xs text-[#5C705E]">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(p.date).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        {p.readMin} min
                      </span>
                    </div>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#1B5E20]">
                      Lire l'article
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}
